/**
 * App Check recovery banner (admin portal).
 *
 * Replaces the opaque `auth/firebase-app-check-token-is-invalid` toast with
 * an actionable surface: reset throttle state, open in incognito, or contact
 * support. Mirrors apps/client/src/components/auth/AppCheckRecoveryBanner.tsx.
 *
 * Styling matches the existing LoginPage error palette (red-tinted glass
 * panel on the founder-portal gradient background) so the banner feels
 * native rather than bolted on.
 */

import React, { useState } from 'react';
import { Shield, RefreshCw, Eye, Mail } from 'lucide-react';
import type { AppCheckErrorContext } from '../../lib/appCheckRecovery';

interface AppCheckRecoveryBannerProps {
  ctx: AppCheckErrorContext;
  onReset: () => void | Promise<void>;
}

const SUPPORT_EMAIL = 'support@valutten.com';
const COPIED_HINT_MS = 4000;

const buildSupportMailto = (ctx: AppCheckErrorContext): string => {
  const errorLike = ctx.raw as { code?: string; message?: string } | null;
  const code = errorLike && typeof errorLike.code === 'string' ? errorLike.code : 'unknown';
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown';
  const timestamp = new Date().toISOString();
  const body = [
    'Hi VALUTTEN team,',
    '',
    'I am unable to sign in to the admin portal due to a browser security check failure.',
    '',
    `Timestamp: ${timestamp}`,
    `Error code: ${code}`,
    `User agent: ${userAgent}`,
    '',
    'Thanks,',
  ].join('\n');
  const subject = encodeURIComponent('Admin App Check login failure');
  return `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${encodeURIComponent(body)}`;
};

const AppCheckRecoveryBanner: React.FC<AppCheckRecoveryBannerProps> = ({ ctx, onReset }) => {
  const [resetting, setResetting] = useState<boolean>(false);
  const [incognitoHint, setIncognitoHint] = useState<boolean>(false);

  const handleReset = async (): Promise<void> => {
    if (resetting) return;
    setResetting(true);
    try {
      await onReset();
    } finally {
      setResetting(false);
    }
  };

  const handleIncognito = async (): Promise<void> => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const url = `${origin}/login`;
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(url);
      }
    } catch {
      // Clipboard may be unavailable (insecure context, permissions); fall
      // through to the visible hint so the user can copy the URL manually
      // from their browser's address bar.
    }
    setIncognitoHint(true);
    window.setTimeout(() => setIncognitoHint(false), COPIED_HINT_MS);
  };

  const supportHref = buildSupportMailto(ctx);

  return (
    <div
      className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg space-y-4 text-left"
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <Shield size={20} className="text-red-300 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <div className="space-y-1">
          <p className="text-sm font-semibold text-red-200">Browser security check failed</p>
          <p className="text-xs text-red-200/80 leading-relaxed">
            Your browser session didn&apos;t pass our security check. This is usually caused by
            privacy extensions, DevTools being open, or signing into multiple accounts from the
            same browser.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={handleReset}
          disabled={resetting}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-red-500/30 hover:bg-red-500/40 border border-red-500/40 rounded-md transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <RefreshCw size={16} className={resetting ? 'animate-spin' : ''} aria-hidden="true" />
          {resetting ? 'Resetting…' : 'Reset & try again'}
        </button>

        <button
          type="button"
          onClick={handleIncognito}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-slate-200 bg-slate-800/60 hover:bg-slate-700/70 border border-slate-600/40 rounded-md transition-colors duration-200"
        >
          <Eye size={16} aria-hidden="true" />
          Open in incognito
        </button>
        {incognitoHint && (
          <p className="text-xs text-emerald-300 px-1" role="status">
            URL copied — open Chrome → ⌘⇧N → paste
          </p>
        )}

        <a
          href={supportHref}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-slate-200 bg-slate-800/60 hover:bg-slate-700/70 border border-slate-600/40 rounded-md transition-colors duration-200"
        >
          <Mail size={16} aria-hidden="true" />
          Contact support
        </a>
      </div>
    </div>
  );
};

export default AppCheckRecoveryBanner;
